import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const SCAM_CARDS = [
  {
    t: "The post office site that worked in two stages",
    b: "It began innocently — a message saying a parcel couldn't be delivered, asking only to confirm the delivery address. That felt completely normal, so Sudheer entered it. Then the page rerouted to a payment request — a small fee to 'attempt delivery again.' That was the moment the doubt arrived. A genuine redelivery is reconfirmed by a phone call, or you simply collect from the nearby post office — they never ask you to pay through a link. He checked the website address. It was a fake replica of the real India Post site. The address he'd already entered was the real target; the fee was the second hook. That exact two-stage trap became a core lesson in the curriculum.",
  },
  {
    t: "The 'parcel stuck in customs' calls",
    b: "Repeated calls claiming a package from abroad was held in customs and needed a fee to release. Pay the small amount, and the next call comes — now from a 'CBI officer,' claiming the parcel contained drugs. This is how a digital-arrest scam begins. Sudheer received these calls many times. He recognised them and ignored them — but knew most people wouldn't.",
  },
  {
    t: "The lesson underneath all of them",
    b: "None of these frauds work on the ignorant. They work on the unprepared. If they could reach an engineer who builds telecom networks for a living, they can reach anyone — which is exactly why the defence has to be taught, early and deliberately.",
  },
];

const BELIEFS = [
  "Practical life knowledge is not a luxury subject. It is a basic right.",
  "Awareness, not expertise — being aware enough to decide well and get real value from any expert.",
  "Knowing is not enough. People are protected by trained reflexes — not by information they once read.",
  "The goal is never fear. It is quiet confidence — a young person who can handle what comes.",
  "This belongs in every school — not only the ones that can afford something extra.",
];

export default function About() {
  useReveal();
  useEffect(() => { document.title = "About — LifeIQ"; }, []);

  return (
    <main>
      {/* SECTION 1 — HERO */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Our story</div>
            <h1 className="mt-5 font-serif-d text-[44px] sm:text-6xl lg:text-[72px] leading-[1.04] tracking-tight">
              From a family of teachers, to the lesson schools forgot to teach.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              LifeIQ was not built by an education company. It was built by an engineer, raised by teachers, who kept being asked the same real-life questions by everyone around him — and realised almost no one had been taught the answers.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — THE BEGINNING */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">The beginning</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              It started at home — in more ways than one.
            </h2>
          </div>

          <div className="mt-10 space-y-7 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            <p>
              Kunti Sudheer Bose grew up surrounded by education. Both his parents were teachers who went on to become school principals in private and public schools, including Sri Saraswathi Shishu Mandir — the very school he studied in himself. Education was not a career choice in his family. It was the air at home.
            </p>
            <p>
              His own path led elsewhere first. A diploma in Electronics and Instrumentation from KDR Government Polytechnic in Wanaparthy, Mahabubnagar district, then a B.Tech in Electronics and Communication from KITS, Karimnagar. From there, years in the telecom industry — with companies including TCS, Ericsson, Airtel, Telenor, Uninor, GTL, and Aster — building and running the networks that quietly connect millions of people.
            </p>
          </div>

          <figure className="mt-14 reveal">
            <blockquote className="font-serif-d italic text-[26px] sm:text-[34px] leading-[1.25] text-[#F57F17] border-l-[3px] border-[#F57F17] pl-7">
              "I came from a family of teachers and a career in engineering. The gap I noticed sat exactly where those two worlds meet."
            </blockquote>
          </figure>
        </div>
      </section>

      {/* SECTION 3 — THE THING HE KEPT NOTICING */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">What he kept seeing</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              The same questions, from everyone.
            </h2>
          </div>

          <div className="mt-10 space-y-7 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            <p>
              Over the years, something kept happening. Friends and family turned to Sudheer for the decisions they felt unprepared to make on their own — which insurance to take, how to think about a financial choice, whether something added up. At a friend's real estate company, he watched people make some of the biggest decisions of their lives — buying property — with very little of the knowledge such a decision deserves.
            </p>
            <p>
              And then there was his daughter. Watching her move through a demanding, capable education, he saw the same gap he saw in the adults around him — only earlier. She could excel at everything school measured. But the things life would actually demand of her? Those were nowhere on the syllabus.
            </p>
            <p>
              Out of all those conversations, one belief slowly took shape — the idea that now sits at the centre of everything LifeIQ teaches. No one can be an expert in every field, and no one should have to be. But everyone deserves to be aware enough to make their own decisions well — to sense when something is wrong, to avoid being misled, and to get genuine value from an expert and judge what's fair to pay. Awareness, not expertise, is what lets an ordinary person navigate life on their own terms.
            </p>
          </div>

          <figure className="mt-14 reveal">
            <blockquote className="font-serif-d italic text-[26px] sm:text-[34px] leading-[1.25] text-[#F57F17] border-l-[3px] border-[#F57F17] pl-7">
              "What would my daughter actually need to know to handle life — not just to pass exams, but really?"
            </blockquote>
          </figure>

          <div className="mt-12 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            <p>
              That question became a list. The list became chapters. The chapters became LifeIQ. And here is the honest part, the part Sudheer says openly: many of the things he now wants children to learn, he had to learn himself while building this. LifeIQ is, in his words, a journey from 'no — I don't know this either' to hearing 'yes' from the next generation.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHEN IT STOPPED BEING THEORETICAL */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">When it stopped being theoretical</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Then the scams came for him too.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              The need for this wasn't proven in a research paper. It was proven in Sudheer's own life — in fraud attempts aimed directly at him, an aware and educated man.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SCAM_CARDS.map((c, i) => (
              <article
                key={i}
                data-testid={`scam-card-${i}`}
                className="reveal pl-6 sm:pl-7 py-2 border-l-[3px] border-[#004D40]"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <h3 className="font-serif-d text-[22px] sm:text-[24px] leading-[1.25] text-[#0D1B2A]">{c.t}</h3>
                <p className="mt-4 text-[16px] text-[#1A1A1A] leading-[1.7]">{c.b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHAT LIFEIQ STANDS FOR */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">What we stand for</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.05] text-white">
              What we believe.
            </h2>
          </div>

          <ul className="mt-14 max-w-3xl space-y-7">
            {BELIEFS.map((b, i) => (
              <li
                key={i}
                data-testid={`belief-${i}`}
                className="reveal font-serif-d text-[22px] sm:text-[28px] lg:text-[30px] leading-[1.3] text-white border-l-[3px] border-[#F57F17] pl-6 sm:pl-7"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 6 — WHERE WE ARE NOW */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">Where we are now</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Built. Now being shared.
            </h2>
          </div>
          <div className="mt-10 space-y-7 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            <p>
              Today LifeIQ is a complete curriculum — three levels, 72 chapters, over a thousand real-life situations, a dedicated cyber-safety module, and full teacher training. It is grounded in real Indian systems, real Indian fraud cases, and real Indian life — written in Hyderabad, for the world these students actually live in.
            </p>
            <p>
              The next step is the most important one: proving it in real classrooms. LifeIQ is working with forward-thinking schools in Hyderabad to run honest pilots — one class, one term, measured before and after — before growing further. Private schools first. Government engagement later, once the outcomes speak for themselves.
            </p>
          </div>

          <figure className="mt-14 reveal">
            <blockquote className="font-serif-d italic text-[22px] sm:text-[28px] leading-[1.35] text-[#F57F17] border-l-[3px] border-[#F57F17] pl-7">
              "LifeIQ exists to reduce the knowledge gap that leads to costly mistakes, poor decisions, unnecessary dependency, and missed opportunities in life."
            </blockquote>
          </figure>
        </div>
      </section>

      {/* SECTION 7 — FROM THE FOUNDER */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">From the founder</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              In his own words.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4 reveal">
              <div
                data-testid="founder-photo"
                className="aspect-square w-full max-w-[320px] overflow-hidden border border-[#D7DADD] bg-[#E5E7EA]"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_beyond-exams/artifacts/dbf1ewhy_Screenshot%202026-06-02%20104045.png"
                  alt="Kunti Sudheer Bose — Founder, LifeIQ"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="mt-4 text-[13px] text-[#5C6470] uppercase tracking-[0.15em]">Kunti Sudheer Bose · Founder</div>
            </div>

            <div className="md:col-span-8 reveal" style={{ transitionDelay: "80ms" }}>
              <p className="font-serif-d text-[22px] sm:text-[26px] leading-[1.45] text-[#0D1B2A]">
                "I didn't set out to start an education company. I come from a family of teachers, I spent my career in telecom engineering, and somewhere in between, I became the person friends called for the questions school never answered — about money, insurance, decisions, scams. Watching my daughter, I realised these gaps start young. I'll be honest: a lot of what I want children to learn through LifeIQ, I had to learn myself while building it. That is exactly why I know it matters. No one can master everything — but everyone deserves to be aware enough to decide well, and to get real value from the experts they turn to. I want the next generation to start where I had to catch up."
              </p>
              <div className="mt-6 text-[14px] text-[#5C6470]">— Kunti Sudheer Bose, Founder, LifeIQ</div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — CTA */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-28 text-center">
          <div className="reveal">
            <h2 className="font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.06] text-white max-w-3xl mx-auto">
              If this resonates, let's talk.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/80 leading-[1.7] max-w-2xl mx-auto">
              Whether you lead a school, teach in one, or simply believe young people deserve this — we would like to hear from you.
            </p>
            <Link to="/contact" data-testid="about-cta" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-8 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              Start a Conversation <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
